// A machine's stats history (#6558): the ranges a caller may ask for, each range's
// bucket width, and the answer's wire shape. One declaration for both doors —
// `GET /v1/runners/:id/stats?range=` and the MCP `box history` action parse the range
// with {@link RunnerStatsRangeSchema} and serve {@link RunnerStatsHistoryWire} — so
// neither can offer a range the other refuses.
//
// The answer carries only real samples (owner ruling G4, #6552): a bucket holding no
// sample is absent, never zero-filled or interpolated, and `collecting_since_iso`
// says how much of the range has data at all.

import { z } from 'zod';

/** The three windows the machine panel charts. */
export const RUNNER_STATS_RANGES = ['1h', '24h', '7d'] as const;
export type RunnerStatsRange = (typeof RUNNER_STATS_RANGES)[number];
export const RunnerStatsRangeSchema = z.enum(RUNNER_STATS_RANGES);

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;

/** How far back each range reaches from now. */
export const RUNNER_STATS_RANGE_MS: Readonly<Record<RunnerStatsRange, number>> = {
  '1h': HOUR_MS,
  '24h': 24 * HOUR_MS,
  '7d': 7 * 24 * HOUR_MS,
};

/**
 * Each range's bucket width: 1 h = 1-minute buckets, 24 h = 15-minute, 7 d = 2-hour.
 * A bucket starts at an epoch multiple of its width, so two reads a minute apart
 * bucket the same samples the same way.
 */
export const RUNNER_STATS_BUCKET_MS: Readonly<Record<RunnerStatsRange, number>> = {
  '1h': MINUTE_MS,
  '24h': 15 * MINUTE_MS,
  '7d': 2 * HOUR_MS,
};

/**
 * One bucket: its start, and the MAXIMUM of each reading's percentage (0-100) over the
 * samples inside it, so the worst minute shows. A reading is null when no sample in the
 * bucket could produce it (an unknown total, or `cpu_cores` 0 / null). Per sample:
 * `cpu_pct` = `100 * load_avg1 / cpu_cores`, `mem_pct` = `100 * (mem_bytes - free_mem_bytes)
 * / mem_bytes`, `disk_pct` = `100 * (ws_total_bytes - ws_free_bytes) / ws_total_bytes`.
 * `cpu_pct` can pass 100 on an oversubscribed machine; it is not clamped.
 */
export type RunnerStatsPointWire = {
  /**
   * The bucket's start, ISO-8601. Buckets align to the epoch, so the first one's start can
   * precede `now - range`; its maximum covers only samples inside the range.
   */
  t: string;
  cpu_pct: number | null;
  mem_pct: number | null;
  disk_pct: number | null;
};

/** The history answer, as both doors publish it. */
export type RunnerStatsHistoryWire = {
  range: RunnerStatsRange;
  /** The oldest sample inside the range, or null when the range holds none. */
  collecting_since_iso: string | null;
  /** The newest sample inside the range, or null when the range holds none. */
  last_sample_iso: string | null;
  /** Oldest first; only buckets that hold at least one sample. */
  points: RunnerStatsPointWire[];
};

/**
 * The runtime half of {@link RunnerStatsHistoryWire}: a reader parses the answer through
 * this, so a body that drifts from the published shape is a named parse error at the seam
 * rather than an `undefined` read deep inside a chart (the machine panel, #6565).
 */
export const RunnerStatsPointWireSchema: z.ZodType<RunnerStatsPointWire> = z.object({
  t: z.string(),
  cpu_pct: z.number().nullable(),
  mem_pct: z.number().nullable(),
  disk_pct: z.number().nullable(),
});

export const RunnerStatsHistoryWireSchema: z.ZodType<RunnerStatsHistoryWire> = z.object({
  range: RunnerStatsRangeSchema,
  collecting_since_iso: z.string().nullable(),
  last_sample_iso: z.string().nullable(),
  points: z.array(RunnerStatsPointWireSchema),
});
